import React, { useState } from 'react';
import {
  required,
  BooleanInput,
  Edit,
  FileField,
  FileInput,
  ImageField,
  ImageInput,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextInput
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Tabs,
  Tab as MuiTab,
  Typography
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

import { handleRelated } from '../utils';
import { TemplateTypes } from './constants';
import { EditTabPanel } from './components';

const EditBetaTab = () => {
  const betaEnabled = useWatch({ name: 'betaEnabled' });

  return (
    <>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <BooleanInput source="betaEnabled" label="Enable Beta" />
        </CardContent>
      </Card>
      {betaEnabled && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <FolderIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Directory & Files
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TextInput source="betaEditorDirectory" label="Beta editor directory" helperText="Beta editor path e.g. /unity-editor-beta/" fullWidth />
                <ReferenceInput label="Beta Template File" reference="template-files" source="betaTemplateFile" perPage={100}>
                  <SelectInput optionText="name" format={handleRelated} fullWidth />
                </ReferenceInput>
                <FileInput source="betaFile" label="Beta File Direct">
                  <FileField source="src" title="title" />
                </FileInput>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <InsertDriveFileIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Config
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <FileInput source="betaDefaultConfig" label="Beta Config JSON" accept="application/json">
                  <FileField source="src" title="title" />
                </FileInput>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </>
  );
};

// -- Production tab edit fields --

const EditProductionTab = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            <FolderIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
            Directory & Files
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextInput source="editorDirectory" label="Editor directory" helperText="Absolute path e.g. /unity-editor/" fullWidth />
          <ReferenceInput label="Template File" reference="template-files" source="templateFile" perPage={100}>
            <SelectInput optionText="name" format={handleRelated} fullWidth />
          </ReferenceInput>
          <FileInput source="file" label="Template File Direct">
            <FileField source="src" title="title" />
          </FileInput>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            <InsertDriveFileIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
            Config
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FileInput source="defaultConfig" label="Default Config JSON" accept="application/json" validate={[required()]}>
            <FileField source="src" title="title" />
          </FileInput>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// -- General tab edit fields --

const EditGeneralTab = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={8}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Details</Typography>
          <Divider sx={{ mb: 2 }} />
          <TextInput source="name" validate={[required()]} fullWidth />
          <TextInput source="version" fullWidth />
          <SelectInput source="type" choices={TemplateTypes} validate={[required()]} fullWidth />
          <BooleanInput source="hasHotspots" />
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Status</Typography>
          <Divider sx={{ mb: 2 }} />
          <BooleanInput source="disabled" />
          <BooleanInput source="featured" />
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={4}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Thumbnail</Typography>
          <Divider sx={{ mb: 2 }} />
          <ImageInput source="thumbnail" label="" accept="image/*" validate={[required()]}>
            <ImageField source="src" title="title" />
          </ImageInput>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// -- Tabbed edit form content --

const TABS = ['General', 'Production', 'Beta'];

const EditFormContent = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' }
          }}
        >
          {TABS.map(label => <MuiTab key={label} label={label} />)}
        </Tabs>
      </Box>

      <EditTabPanel active={tab === 0}>
        <EditGeneralTab />
      </EditTabPanel>

      <EditTabPanel active={tab === 1}>
        <EditProductionTab />
      </EditTabPanel>

      <EditTabPanel active={tab === 2}>
        <EditBetaTab />
      </EditTabPanel>
    </Box>
  );
};

// -- Edit page wrapper --

const TemplateEdit = props => (
  <Edit redirect="show" {...props}>
    <SimpleForm sx={{ p: 0, '& .RaSimpleForm-content': { p: 0 }, '& > .MuiCardContent-root': { p: 0 } }}>
      <EditFormContent />
    </SimpleForm>
  </Edit>
);

export default TemplateEdit;
